<template>
    <g
        v-if="!(filtered()) && node1"
        class="conn"
        @mouseenter="hovered=true;"
        @mouseleave="hovered=false;"
    >
        <!-- invisible line to make clicking easier -->
        <line
            v-if="node1 && (node2 || overridePos)"
            :x1="connStartPos.x"
            :y1="connStartPos.y"
            :x2="connEndPos.x"
            :y2="connEndPos.y"
            stroke="transparent"
            :stroke-width="40 * scale"
            @click="connClick"
        />
        <!-- rendered line -->
        <line
            v-if="node1 && (node2 || overridePos)"
            :class="overridePos ? 'conn-line-custom-col' : 'conn-line'"
            :x1="connStartPos.x"
            :y1="connStartPos.y"
            :x2="connEndPos.x"
            :y2="connEndPos.y"
            :stroke="color"
            :stroke-width="4 * scale"
            @click="connClick"
        />
        <!-- arrowhead -->
        <polygon
            v-if="renderArrowhead1()"
            class="conn"
            :points="arrowPoints"
            :style="`transition: fill 100ms;fill:${color};`"
            @click="connClick"
        />
        <!-- possible second arrowhead -->
        <polygon
            v-if="renderArrowhead2()"
            class="conn"
            :points="inverseArrowPoints"
            :style="`transition: fill 100ms;fill:${color};`"
            @click="connClick"
        />
        <!--
		<text v-if="false" :x="connStartPos.x" :y="connStartPos.y + 100">
			DEBUG TEXT: {{scale}}
		</text>
		-->
    </g>
</template>

<script>
import Vue from 'vue';
import Connection, { ConnectionDirection } from '@/models/Connection';
// eslint-disable-next-line no-unused-vars
import Node from '@/models/Node';
import Vector2 from '@/models/Vector2';
import Mutations from '@/store/mutations';

export default Vue.extend({
    props:
	{
	    connection : Connection,
	    scale : {
	        type: Number,
	        default: 1
	    },
		
	    // TODO: Figure out a portable solution here.  Either we get
	    // warnings in other browsers for failed type checks or Edge
	    // flat out doesn't work.  Neither of which is great.
	    parentBounds : {
	        // The portable solution is just to nix the type check.  Ezpz.
	        //type: /*DOMRect ? DOMRect :*/ Object,
	        default: null
	    },
	    camera : Object,
	    overridePos : Object
	},
    data()
    {
        return {
            hovered: false,
            transition: null,
            reqAnimFrame: null,
            shouldLiveTransition: false,
			
            // This is dirty, but meh for now
            transitionProcessing: false
        };
    },
    computed:
	{
	    triangleLength()
	    {
	        return 25 * this.scale;
	    },
	    triangleWidth()
	    {
	        return 15 * this.scale;
	    },
	    node1() 
	    {
	        return this.$store.getters.nodes.get(this.connection.nodeid1);
	    },
	    node2() 
	    {
	        return this.$store.getters.nodes.get(this.connection.nodeid2);
	    },
	    color() 
	    {
	        if (this.overridePos)
	        {
	            if (this.node2)
	            {
	                return 'green';
	            }
	            return 'red';
	        }
	        if (this.hovered)
	        {
	            return 'yellow';
	        }
	        return 'rgba(32, 32, 32, .95)';
	    },
	    arrowPoints()
	    {
	        let radius = this.node2 ? this.calculateNodeCircleRadius(this.node2) : 0;
	        return this.calculateTrianglePoints(this.startPos, this.endPos, radius);
	    },
	    inverseArrowPoints()
	    {
	        let radius = this.calculateNodeCircleRadius(this.node1);
	        return this.calculateTrianglePoints(this.endPos, this.startPos, radius);
	    },
	    connStartPos()
	    {
	        if (!this.node2 || this.node2.dateStart != this.node1.dateStart)
	            return this.startPos;
			
	        let radius = this.calculateNodeCircleRadius(this.node1);
	        return this.startPos.sub(this.startEndDir.scale(-radius));
	    },
	    connEndPos()
	    {
	        if (!this.node2 || this.node2.dateStart < this.node1.dateStart)
	            return this.endPos;
			
	        let radius = this.node2 ? this.calculateNodeCircleRadius(this.node2) : 0;
	        return this.endPos.sub(this.startEndDir.scale(radius));
	    },
	    startEndDir()
	    {
	        let distVec = this.endPos.sub(this.startPos);
	        distVec.normalize();
	        return distVec;
	    }
	},
    watch:
	{
	    "camera.minDate":
		{
		    deep: true,
		    handler()
		    {
		        this.manageTransition(10);
		    }
		},
	    "node1.component.bound":
		{
		    deep: true,
		    handler()
		    {
		        this.manageTransition();
		    }
		},
	    "node2.component.bound":
		{
		    deep: true,
		    handler()
		    {
		        this.manageTransition();
		    }
		},
	},
    mounted()
    {
        let waitForNode = () =>
        {
            // TODO: Should we wait for node2 here as well?
            if (this.node1 && this.node1.component)
            {
                // Ensure that it follows the nodes if they transition into place
                this.manageTransition();
                setTimeout(() =>
                {
                    this.manageTransition();
                }, 30);

                console.log("connection found node");
                this.$forceUpdate();
                return;
            }
            // console.log('could not find node, waiting...');
            setTimeout(waitForNode, 0);
        };
        waitForNode();
    },
    beforeDestroy()
    {
        this.shouldLiveTransition = false;
        this.transitionProcessing = false;
        if (this.transition != null)
        {
            clearTimeout(this.transition);
        }
        if (this.reqAnimFrame != null)
        {
            cancelAnimationFrame(this.reqAnimFrame);
        }
    },
    recomputed:
	{
	    startPos()
	    {
	        return this.calculatePointFromNode(this.node1);
	    },
	    endPos()
	    {
	        if (this.overridePos)
	        {
	            // It's -1 if we're manually overriding
	            if (this.connection.nodeid2 != -1)
	            {
	                // If it's not -1 and node2 is invalid, we must recompute node2.
	                if (!this.node2)
	                {
	                    //this.$recompute('node2')
	                }
	            }
	            else
	            {
	                return this.overridePos;
	            }
	        }
			
	        return this.calculatePointFromNode(this.node2);
	    },
	},
    methods:
	{
	    renderForwardArrowhead()
	    {
	        return this.connection.direction == ConnectionDirection.Forward
				|| this.connection.direction == ConnectionDirection.Bidirectional;
	    },
	    renderBackwardArrowhead()
	    {
	        return this.connection.direction == ConnectionDirection.Backward
				|| this.connection.direction == ConnectionDirection.Bidirectional;
	    },
	    renderArrowhead1()
	    {
	        return ((!(this.node2) || this.node2.dateStart >= this.node1.dateStart) && this.renderForwardArrowhead())
				|| ((this.node1 && this.node2 && this.node1.dateStart >= this.node2.dateStart) && this.renderBackwardArrowhead());
	    },
	    renderArrowhead2()
	    {
	        return ((!(this.node2) || this.node2.dateStart >= this.node1.dateStart) && this.renderBackwardArrowhead())
				|| ((this.node1 && this.node2 && this.node1.dateStart >= this.node2.dateStart) && this.renderForwardArrowhead());
	    },
	    calculateTrianglePoints(startPos, endPos, radius=0)
	    {
	        let points = [];

	        const triLen = this.triangleLength;
	        const triWid = this.triangleWidth;

	        let distVec = endPos.sub(startPos);
	        distVec.normalize();
	        let tan = new Vector2(distVec.y, -distVec.x);

	        let arrowEnd = radius > 0 ? endPos.sub(distVec.scale(radius)) : endPos;
	        points.push(arrowEnd);
	        points.push(new Vector2(arrowEnd.x + -distVec.x * triLen + tan.x * triWid, arrowEnd.y + -distVec.y * triLen + tan.y * triWid));
	        points.push(new Vector2(arrowEnd.x + -distVec.x * triLen - tan.x * triWid, arrowEnd.y + -distVec.y * triLen - tan.y * triWid));
			
	        let res = "";
	        for(let i = 0; i < points.length; ++i)
	        {
	            let pt = points[i];
	            res += (`${pt.x},${pt.y}`);
	            if (i < points.length - 1)
	            {
	                res += ' ';
	            }
	        }
	        return res;
	    },
	    manageTransition()
	    {
	        let interval = 600;
	        if (this.transition != null)
	        {
	            clearTimeout(this.transition);
	        }
	        if (this.reqAnimFrame != null)
	        {
	            cancelAnimationFrame(this.reqAnimFrame);
	        }
			
	        this.transition = setTimeout(() => 
	        {
	            this.shouldLiveTransition = false;
	            this.transition = null;
	        }, interval);

	        this.shouldLiveTransition = true;
	        let liveUpdate = ()=>
	        {
	            this.transitionProcessing=true;
	            if (this.shouldLiveTransition)
	            {
	                this.$recompute('startPos');
	                this.$recompute('endPos');
	                this.reqAnimFrame = requestAnimationFrame(liveUpdate);
	            }
	            else
	            {
	                this.$nextTick(()=>
	                {
	                    this.$recompute('startPos');
	                    this.$recompute('endPos');
	                });
	            }
	            this.transitionProcessing=false;
	        };
	        this.reqAnimFrame = requestAnimationFrame(liveUpdate);
	    },
	    calculatePointFromNode(node)
	    {
	        if (node == null)
	        {
	            return {x: 0, y: 0};
	        }
	        else if (node.component == null || node.component.$refs.nodeBar == null)
	        {
	            const pixelsPerYear = this.camera.yearToPixels;
	            const pixelsPerSecond = pixelsPerYear / 31556926; // 31556926 == seconds in a year
	            const secondsSinceEpoch = (node.dateStart.getTime() / 1000);
	            const secondsSinceYearZero = secondsSinceEpoch + 62167219200;
	            const xPos = (pixelsPerSecond * secondsSinceYearZero) - (this.camera.minDate * this.camera.yearToPixels);
	            const yPos = this.parentBounds.height - node.position.y - 45;
	            return new Vector2(xPos, yPos);
	        }

	        // Listen for changes in the camera position or start position
	        node.component.bound;
			
	        // This is dirty, but meh for now
	        // Doing it this way because a watcher wasn't working
	        if (!this.transitionProcessing)
	        {
	            //this.manageTransition();
	        }

	        /** @type {DOMRect} */
	        let bounds = node.component.$refs.nodeBar.getBoundingClientRect();
	        // console.log(new Vector2(
	        // 	bounds.left - (this).parentBounds.left,
	        // 	bounds.top - (this).parentBounds.top + (bounds.bottom - bounds.top) / 2
	        // ));
	        return new Vector2(
	            bounds.left - (this).parentBounds.left,
	            bounds.top - (this).parentBounds.top + (bounds.bottom - bounds.top) / 2
	        );
	    },
	    calculateNodeCircleRadius(node)
	    {
	        if (node == null || node.component == null || node.component.$refs.nodeCircle == null)
	        {
	            // console.log(node);
	            return 0;
	        }
	        let bounds = node.component.$refs.nodeCircle.getBoundingClientRect();
	        return (bounds.right - bounds.left) / 2;
	    },
	    connClick(ev)
	    {
	        if (this.overridePos)
	            return;
			
	        this.$store.commit(Mutations.setSelected, this.connection);
	    },
	    filtered() 
	    {
	        let tag1 = null;
	        if (this.node1)
	        {
	            for (let tag of this.node1.tags)
	            {
	                if (tag.type == "category")
	                {
	                    tag1 = tag;
	                }
	            }
	        }
	        let tag2 = null;
	        if (this.node2)
	        {
	            for (let tag of this.node2.tags)
	            {
	                if (tag.type == "category")
	                {
	                    tag2 = tag;
	                }
	            }
	        }
	        return (tag1 && tag1.filtered) || (tag2 && tag2.filtered);
	    }
	}
});
</script>

<style lang="scss" scoped>
.conn-line
{
	transition: stroke 100ms;
}

.conn-line-custom-col
{
	transition: stroke 100ms;
}

.conn:hover
{
	cursor: pointer;
	stroke: yellow;
	stroke-width: 2px;
}
</style>